import React, { useEffect, useState } from 'react';
import api from '../services/api';

interface FamilyMember {
  id: number;
  name: string;
  relation: string;
  age?: number;
  birthDate?: string;
  lastContact?: string;
  notes?: string;
}

interface FamilyEvent {
  id: number | string;
  title: string;
  eventDate: string;
  eventType: string;
  familyMember?: { name: string };
  isAutoGenerated?: boolean;
}

interface FamilyGoal {
  id: number;
  goalId: number;
  familyMembersInvolved: string[];
  notes?: string;
  goal?: {
    id: number;
    name: string;
    status: string;
    dueDate?: string;
  };
}

interface Goal {
  id: number;
  name: string;
  status: string;
  description?: string;
  dueDate?: string;
  progress?: number;
}

interface RelationshipStatus {
  status: string;
  partnerName?: string;
  anniversaryDate?: string;
  notes?: string;
}

const Family: React.FC = () => {
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [relationshipStatus, setRelationshipStatus] = useState<RelationshipStatus | null>(null);
  const [events, setEvents] = useState<FamilyEvent[]>([]);
  const [familyGoals, setFamilyGoals] = useState<FamilyGoal[]>([]);
  const [allGoals, setAllGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(true);

  const [showAddMember, setShowAddMember] = useState(false);
  const [showEditMember, setShowEditMember] = useState(false);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [showEditEvent, setShowEditEvent] = useState(false);
  const [showAddFamilyGoal, setShowAddFamilyGoal] = useState(false);
  const [showEditStatus, setShowEditStatus] = useState(false);

  const [eventFilter, setEventFilter] = useState<'all' | 'active' | 'completed'>('active');
  const [goalFilter, setGoalFilter] = useState<'all' | 'active' | 'completed'>('active');

  const [newMember, setNewMember] = useState({ name: '', relation: '', age: '', birthDate: '', notes: '' });
  const [editingMember, setEditingMember] = useState<FamilyMember | null>(null);
  const [newEvent, setNewEvent] = useState({ title: '', eventDate: '', eventType: '', familyMemberId: '' });
  const [editingEvent, setEditingEvent] = useState<FamilyEvent | null>(null);
  const [newFamilyGoal, setNewFamilyGoal] = useState({ goalId: '', familyMembers: '', notes: '' });
  const [newStatus, setNewStatus] = useState({ status: 'single', partnerName: '', anniversaryDate: '', notes: '' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [familyRes, statusRes, eventsRes, familyGoalsRes, goalsRes] = await Promise.all([
        api.get('/api/family/members'),
        api.get('/api/family/relationship-status'),
        api.get('/api/family/events'),
        api.get('/api/family/family-goals'),
        api.get('/api/goals')
      ]);

      setFamilyMembers(familyRes.data);
      setRelationshipStatus(statusRes.data || null);
      setEvents(eventsRes.data);
      setFamilyGoals(familyGoalsRes.data);
      setAllGoals(goalsRes.data);
    } catch (error) {
      console.error('Error loading family data:', error);
    } finally {
      setLoading(false);
    }
  };

  const addMember = async () => {
    try {
      const memberData = { ...newMember, age: newMember.age ? parseInt(newMember.age) : null };
      await api.post('/api/family/members', memberData);
      loadData();
      setShowAddMember(false);
      setNewMember({ name: '', relation: '', age: '', birthDate: '', notes: '' });
    } catch (error) {
      console.error('Error adding member:', error);
    }
  };

  const editMember = (member: FamilyMember) => {
    setEditingMember(member);
    setShowEditMember(true);
  };

  const updateMember = async () => {
    if (!editingMember) return;
    try {
      const memberData = {
        name: editingMember.name,
        relation: editingMember.relation,
        age: editingMember.age || null,
        birthDate: editingMember.birthDate || null,
        notes: editingMember.notes || ''
      };
      await api.put(`/api/family/members/${editingMember.id}`, memberData);
      loadData();
      setShowEditMember(false);
      setEditingMember(null);
    } catch (error) {
      console.error('Error updating member:', error);
    }
  };

  const deleteMember = async (id: number) => {
    if (!window.confirm('Удалить этого члена семьи?')) return;
    try {
      await api.delete(`/api/family/members/${id}`);
      loadData();
    } catch (error) {
      console.error('Error deleting member:', error);
    }
  };

  const addEvent = async () => {
    try {
      if (!newEvent.title || !newEvent.eventDate || !newEvent.eventType) {
        alert('Пожалуйста, заполните все обязательные поля');
        return;
      }

      const eventData = {
        title: newEvent.title,
        eventDate: newEvent.eventDate,
        eventType: newEvent.eventType,
        familyMemberId: newEvent.familyMemberId ? parseInt(newEvent.familyMemberId) : null
      };
      await api.post('/api/family/events', eventData);
      loadData();
      setShowAddEvent(false);
      setNewEvent({ title: '', eventDate: '', eventType: '', familyMemberId: '' });
    } catch (error) {
      console.error('Error adding event:', error);
      alert('Ошибка при добавлении события');
    }
  };

  const editEvent = (event: FamilyEvent) => {
    setEditingEvent({
      ...event,
      familyMember: event.familyMember
    });
    setShowEditEvent(true);
  };

  const updateEvent = async () => {
    if (!editingEvent) return;
    try {
      if (!editingEvent.title || !editingEvent.eventDate || !editingEvent.eventType) {
        alert('Пожалуйста, заполните все обязательные поля');
        return;
      }

      const eventData = {
        title: editingEvent.title,
        eventDate: editingEvent.eventDate,
        eventType: editingEvent.eventType,
        familyMemberId: typeof editingEvent.id === 'number' && editingEvent.familyMember ? 
          (familyMembers.find(m => m.name === editingEvent.familyMember?.name)?.id || null) : null
      };
      
      await api.put(`/api/family/events/${editingEvent.id}`, eventData);
      loadData();
      setShowEditEvent(false);
      setEditingEvent(null);
    } catch (error) {
      console.error('Error updating event:', error);
      alert('Ошибка при обновлении события');
    }
  };

  const deleteEvent = async (id: number | string) => {
    try {
      await api.delete(`/api/family/events/${id}`);
      loadData();
    } catch (error) {
      console.error('Error deleting event:', error);
    }
  };

  const addFamilyGoal = async () => {
    try {
      const goalData = {
        goalId: parseInt(newFamilyGoal.goalId),
        familyMembersInvolved: newFamilyGoal.familyMembers.split(',').map(m => m.trim()).filter(m => m),
        notes: newFamilyGoal.notes || null
      };
      await api.post('/api/family/family-goals', goalData);
      loadData();
      setShowAddFamilyGoal(false);
      setNewFamilyGoal({ goalId: '', familyMembers: '', notes: '' });
    } catch (error) {
      console.error('Error adding family goal:', error);
    }
  };

  const deleteFamilyGoal = async (id: number) => {
    try {
      await api.delete(`/api/family/family-goals/${id}`);
      loadData();
    } catch (error) {
      console.error('Error deleting family goal:', error);
    }
  };

  const updateStatus = async () => {
    try {
      const statusData = {
        status: newStatus.status,
        partnerName: newStatus.partnerName || null,
        anniversaryDate: newStatus.anniversaryDate || null,
        notes: newStatus.notes || null
      };
      console.log('Отправляем данные:', statusData);
      const response = await api.post('/api/family/relationship-status', statusData);
      console.log('Ответ сервера:', response);
      loadData();
      setShowEditStatus(false);
    } catch (error: any) {
      console.error('Error updating status:', error);
      console.error('Детали ошибки:', error.response?.data);
    }
  };

  const statusLabels: Record<string, string> = {
    single: 'Одинокий(ая)',
    dating: 'В отношениях',
    engaged: 'Помолвлен(а)',
    married: 'Женат/Замужем',
    divorced: 'Разведен(а)',
    widowed: 'Вдовец/Вдова'
  };

  if (loading) {
    return <div style={{ padding: '20px' }}>Загрузка...</div>;
  }

  // Filter events
  const filteredEvents = events.filter(event => {
    if (eventFilter === 'all') return true;
    const eventDate = new Date(event.eventDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    eventDate.setHours(0, 0, 0, 0);
    
    if (eventFilter === 'active') {
      // Активные события: дата события >= сегодня (сегодня и будущие)
      return eventDate >= today;
    } else {
      // Завершенные события: дата события < сегодня (прошедшие)
      return eventDate < today;
    }
  });

  // Filter goals
  const filteredFamilyGoals = familyGoals.filter(familyGoal => {
    if (goalFilter === 'all') return true;
    if (!familyGoal.goal) return false;
    
    if (goalFilter === 'active') {
      return familyGoal.goal.status !== 'completed';
    } else {
      return familyGoal.goal.status === 'completed';
    }
  });

  return (
    <div style={{ padding: '30px', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh' }}>
      <h1 style={{ fontSize: '36px', fontWeight: '700', color: '#2d3748', marginBottom: '30px', textShadow: '2px 2px 4px rgba(0,0,0,0.1)' }}>
        👨‍👩‍👧‍👦 Семья
      </h1>

      {/* Relationship Status */}
      <div style={{ marginBottom: '30px', padding: '25px', background: 'linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%)', borderRadius: '16px', boxShadow: '0 8px 20px rgba(0,0,0,0.1)' }}>
        <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#2d3748', marginTop: 0, marginBottom: '20px' }}>💑 Статус отношений</h2>
        {relationshipStatus ? (
          <div>
            <p><strong>Статус:</strong> {statusLabels[relationshipStatus.status] || relationshipStatus.status}</p>
            {relationshipStatus.partnerName && <p><strong>Партнёр:</strong> {relationshipStatus.partnerName}</p>}
            {relationshipStatus.anniversaryDate && (
              <p><strong>Годовщина:</strong> {new Date(relationshipStatus.anniversaryDate).toLocaleDateString()}</p>
            )}
            {relationshipStatus.notes && <p><strong>Заметки:</strong> {relationshipStatus.notes}</p>}
            <button onClick={() => {
              setNewStatus({
                status: relationshipStatus.status || 'single',
                partnerName: relationshipStatus.partnerName || '',
                anniversaryDate: relationshipStatus.anniversaryDate || '',
                notes: relationshipStatus.notes || ''
              });
              setShowEditStatus(true);
            }} style={{ 
              marginTop: '15px', 
              padding: '10px 20px', 
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', 
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              boxShadow: '0 4px 15px rgba(240, 147, 251, 0.4)',
              transition: 'transform 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              Изменить статус
            </button>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '15px' }}>💑</div>
            <p style={{ color: '#718096', marginBottom: '15px' }}>Статус не установлен</p>
            <button onClick={() => {
              setNewStatus({ status: 'single', partnerName: '', anniversaryDate: '', notes: '' });
              setShowEditStatus(true);
            }} style={{ 
              padding: '10px 20px', 
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              boxShadow: '0 4px 15px rgba(240, 147, 251, 0.4)',
              transition: 'transform 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              Установить статус
            </button>
          </div>
        )}
      </div>

      {/* Family Members */}
      <div style={{ marginBottom: '30px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#2d3748', margin: 0 }}>👥 Члены семьи</h2>
          <button 
            onClick={() => setShowAddMember(true)} 
            style={{ 
              padding: '10px 20px', 
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', 
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              boxShadow: '0 4px 15px rgba(79, 172, 254, 0.4)',
              transition: 'transform 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            + Добавить члена семьи
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
          {familyMembers.map(member => (
            <div 
              key={member.id} 
              style={{ 
                padding: '20px', 
                background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)', 
                border: 'none',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
              }}
            >
              <h3 style={{ margin: '0 0 10px 0', color: '#2d3748', fontSize: '20px' }}>{member.name || 'Без имени'}</h3>
              <p style={{ color: '#4a5568', marginBottom: '5px' }}><strong>Отношение:</strong> {member.relation || 'Не указано'}</p>
              {member.age && <p style={{ color: '#4a5568', marginBottom: '5px' }}><strong>Возраст:</strong> {member.age}</p>}
              {member.birthDate && <p style={{ color: '#4a5568', marginBottom: '5px' }}><strong>🎂 Дата рождения:</strong> {new Date(member.birthDate).toLocaleDateString()}</p>}
              {member.notes && <p style={{ fontSize: '14px', color: '#718096', marginTop: '10px', fontStyle: 'italic' }}>{member.notes}</p>}
              <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                <button 
                  onClick={() => editMember(member)} 
                  style={{ 
                    flex: 1, 
                    padding: '8px 16px', 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '8px', 
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '14px'
                  }}
                >
                  Изменить
                </button>
                <button 
                  onClick={() => deleteMember(member.id)} 
                  style={{ 
                    flex: 1, 
                    padding: '8px 16px', 
                    background: 'linear-gradient(135deg, #f56565 0%, #c53030 100%)', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '8px', 
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '14px'
                  }}
                >
                  Удалить
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Events */}
      <div style={{ marginBottom: '30px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#2d3748', margin: 0 }}>📅 События</h2>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <select 
              value={eventFilter} 
              onChange={(e) => setEventFilter(e.target.value as 'all' | 'active' | 'completed')}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: '2px solid #e2e8f0',
                background: 'white',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                color: '#4a5568'
              }}
            >
              <option value="active">Активные</option>
              <option value="completed">Завершенные</option>
              <option value="all">Все</option>
            </select>
            <button 
              onClick={() => setShowAddEvent(true)} 
              style={{ 
                padding: '10px 20px', 
                background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', 
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                boxShadow: '0 4px 15px rgba(250, 112, 154, 0.4)',
                transition: 'transform 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              + Добавить событие
            </button>
          </div>
        </div>

        {filteredEvents.length > 0 ? (
          <div style={{ background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)', borderRadius: '12px', padding: '20px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)' }}>
            {filteredEvents.map(event => (
              <div key={event.id} style={{ padding: '15px 0', borderBottom: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <strong style={{ fontSize: '16px', color: '#2d3748' }}>{event.title}</strong>
                      {event.isAutoGenerated && (
                        <span style={{ 
                          display: 'inline-block',
                          padding: '2px 8px',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: '600'
                        }}>
                          ⚡ Авто
                        </span>
                      )}
                    </div>
                    <span style={{ marginLeft: '0px', color: '#718096', fontSize: '14px' }}>📅 {new Date(event.eventDate).toLocaleDateString()}</span>
                    <p style={{ fontSize: '14px', color: '#718096', margin: '5px 0 0 0' }}>
                      <span style={{ 
                        display: 'inline-block',
                        padding: '2px 8px',
                        background: '#e6fffa',
                        color: '#0d9488',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {event.eventType}
                      </span>
                      {event.familyMember && <span style={{ marginLeft: '10px' }}>👤 {event.familyMember.name}</span>}
                    </p>
                  </div>
                  {!event.isAutoGenerated && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button 
                        onClick={() => editEvent(event)} 
                        style={{ 
                          padding: '6px 12px', 
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                          color: 'white', 
                          border: 'none', 
                          borderRadius: '6px', 
                          cursor: 'pointer',
                          fontWeight: '600',
                          fontSize: '13px'
                        }}
                      >
                        Изменить
                      </button>
                      <button 
                        onClick={() => deleteEvent(event.id)} 
                        style={{ 
                          padding: '6px 12px', 
                          background: 'linear-gradient(135deg, #f56565 0%, #c53030 100%)', 
                          color: 'white', 
                          border: 'none', 
                          borderRadius: '6px', 
                          cursor: 'pointer',
                          fontWeight: '600',
                          fontSize: '13px'
                        }}
                      >
                        Удалить
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ 
            padding: '40px', 
            background: 'linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%)', 
            borderRadius: '12px', 
            textAlign: 'center',
            border: '2px dashed #cbd5e0'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '10px' }}>📅</div>
            <p style={{ fontSize: '16px', color: '#718096', margin: 0 }}>
              {eventFilter === 'active' ? 'Нет активных событий' : eventFilter === 'completed' ? 'Нет завершенных событий' : 'Нет событий'}
            </p>
          </div>
        )}
      </div>

      {/* Family Goals */}
      <div style={{ marginBottom: '30px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h2>🎯 Семейные цели</h2>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <select 
              value={goalFilter} 
              onChange={(e) => setGoalFilter(e.target.value as 'all' | 'active' | 'completed')}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: '2px solid #e2e8f0',
                background: 'white',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                color: '#4a5568'
              }}
            >
              <option value="active">Активные</option>
              <option value="completed">Завершенные</option>
              <option value="all">Все</option>
            </select>
            <button 
              onClick={() => setShowAddFamilyGoal(true)} 
              style={{ 
                padding: '10px 20px', 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                color: 'white', 
                border: 'none', 
                borderRadius: '8px', 
                cursor: 'pointer',
                fontWeight: '600',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                transition: 'transform 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              + Добавить семейную цель
            </button>
          </div>
        </div>

        {filteredFamilyGoals.length > 0 ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
            {filteredFamilyGoals.map(familyGoal => {
              const progress = allGoals.find(g => g.id === familyGoal.goalId)?.progress ?? 0;
              return (
                <div 
                  key={familyGoal.id} 
                  style={{ 
                    padding: '20px', 
                    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', 
                    borderRadius: '12px', 
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  <div style={{ position: 'absolute', top: 0, right: 0, width: '100px', height: '100px', background: 'rgba(255,255,255,0.1)', borderRadius: '0 0 0 100%' }}></div>
                  <div style={{ position: 'relative', zIndex: 1 }}>
                    <h3 style={{ margin: '0 0 10px 0', color: '#2d3748', fontSize: '18px' }}>
                      {familyGoal.goal?.name || 'Цель удалена'}
                    </h3>
                    {familyGoal.goal && (
                      <div style={{ marginBottom: '10px' }}>
                        <span style={{ 
                          display: 'inline-block',
                          padding: '4px 12px', 
                          background: familyGoal.goal.status === 'completed' ? '#48bb78' : familyGoal.goal.status === 'in_progress' ? '#4299e1' : '#ed8936',
                          color: 'white',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}>
                          {familyGoal.goal.status === 'completed' ? '✓ Завершена' : familyGoal.goal.status === 'in_progress' ? '⏳ В процессе' : '📋 Запланирована'}
                        </span>
                        {familyGoal.goal.dueDate && (
                          <span style={{ marginLeft: '10px', fontSize: '14px', color: '#718096' }}>
                            📅 {new Date(familyGoal.goal.dueDate).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    )}
                    {familyGoal.familyMembersInvolved && familyGoal.familyMembersInvolved.length > 0 && (
                      <p style={{ fontSize: '14px', color: '#4a5568', margin: '10px 0' }}>
                        <strong>👥 Участники:</strong> {familyGoal.familyMembersInvolved.join(', ')}
                      </p>
                    )}
                    {familyGoal.notes && (
                      <p style={{ fontSize: '14px', color: '#718096', marginTop: '10px', fontStyle: 'italic' }}>
                        {familyGoal.notes}
                      </p>
                    )}

                    {/* Progress bar (sourced from allGoals.progress if available) */}
                    <div style={{ marginTop: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                        <strong style={{ color: '#2d3748' }}>Прогресс</strong>
                        <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#4b5563' }}>{progress}%</span>
                      </div>
                      <div style={{ background: '#e6eefc', borderRadius: '8px', height: '10px', overflow: 'hidden' }}>
                        <div style={{ width: `${progress}%`, height: '100%', background: progress === 100 ? '#10b981' : '#3b82f6', transition: 'width 0.6s' }} />
                      </div>
                    </div>

                    <button 
                      onClick={() => deleteFamilyGoal(familyGoal.id)} 
                      style={{ 
                        marginTop: '15px',
                        padding: '8px 16px', 
                        background: '#f56565', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '6px', 
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      Удалить
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ 
            padding: '40px', 
            background: 'linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%)', 
            borderRadius: '12px', 
            textAlign: 'center',
            border: '2px dashed #cbd5e0'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '10px' }}>🎯</div>
            <p style={{ fontSize: '16px', color: '#718096', margin: 0 }}>
              {goalFilter === 'active' ? 'Нет активных семейных целей' : goalFilter === 'completed' ? 'Нет завершенных семейных целей' : 'Нет семейных целей'}
            </p>
            <p style={{ fontSize: '14px', color: '#a0aec0', marginTop: '5px' }}>Добавьте цель из вашего списка личных целей</p>
          </div>
        )}
      </div>

      {/* Modal: Edit Status */}
      {showEditStatus && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', padding: '30px', borderRadius: '8px', width: '400px', maxHeight: '80vh', overflow: 'auto' }}>
            <h3>Редактировать статус отношений</h3>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Статус:</label>
              <select value={newStatus.status} onChange={e => setNewStatus({ ...newStatus, status: e.target.value })} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}>
                <option value="single">Одинокий(ая)</option>
                <option value="dating">В отношениях</option>
                <option value="engaged">Помолвлен(а)</option>
                <option value="married">Женат/Замужем</option>
                <option value="divorced">Разведен(а)</option>
                <option value="widowed">Вдовец/Вдова</option>
              </select>
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Имя партнёра:</label>
              <input type="text" value={newStatus.partnerName} onChange={e => setNewStatus({ ...newStatus, partnerName: e.target.value })} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }} />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Годовщина:</label>
              <input type="date" value={newStatus.anniversaryDate} onChange={e => setNewStatus({ ...newStatus, anniversaryDate: e.target.value })} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }} />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Заметки:</label>
              <textarea value={newStatus.notes} onChange={e => setNewStatus({ ...newStatus, notes: e.target.value })} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', minHeight: '80px' }} />
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={updateStatus} style={{ flex: 1, padding: '10px', background: '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Сохранить</button>
              <button onClick={() => setShowEditStatus(false)} style={{ flex: 1, padding: '10px', background: '#ccc', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Отмена</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Edit Member */}
      {showEditMember && editingMember && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', padding: '30px', borderRadius: '8px', width: '400px', maxHeight: '80vh', overflow: 'auto' }}>
            <h3>Изменить члена семьи</h3>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Имя:</label>
              <input type="text" value={editingMember.name} onChange={e => setEditingMember({ ...editingMember, name: e.target.value })} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }} />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Отношение:</label>
              <input type="text" placeholder="муж, жена, сын, дочь..." value={editingMember.relation} onChange={e => setEditingMember({ ...editingMember, relation: e.target.value })} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }} />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Возраст:</label>
              <input type="number" value={editingMember.age || ''} onChange={e => setEditingMember({ ...editingMember, age: e.target.value ? parseInt(e.target.value) : undefined })} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }} />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Дата рождения:</label>
              <input type="date" value={editingMember.birthDate || ''} onChange={e => setEditingMember({ ...editingMember, birthDate: e.target.value })} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }} />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Заметки:</label>
              <textarea value={editingMember.notes || ''} onChange={e => setEditingMember({ ...editingMember, notes: e.target.value })} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', minHeight: '80px' }} />
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={updateMember} style={{ flex: 1, padding: '10px', background: '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Сохранить</button>
              <button onClick={() => { setShowEditMember(false); setEditingMember(null); }} style={{ flex: 1, padding: '10px', background: '#ccc', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Отмена</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Add Member */}
      {showAddMember && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', padding: '30px', borderRadius: '8px', width: '400px', maxHeight: '80vh', overflow: 'auto' }}>
            <h3>Добавить члена семьи</h3>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Имя:</label>
              <input type="text" value={newMember.name} onChange={e => setNewMember({ ...newMember, name: e.target.value })} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }} />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Отношение:</label>
              <input type="text" placeholder="муж, жена, сын, дочь..." value={newMember.relation} onChange={e => setNewMember({ ...newMember, relation: e.target.value })} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }} />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Возраст:</label>
              <input type="number" value={newMember.age} onChange={e => setNewMember({ ...newMember, age: e.target.value })} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }} />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Дата рождения:</label>
              <input type="date" value={newMember.birthDate} onChange={e => setNewMember({ ...newMember, birthDate: e.target.value })} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }} />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Заметки:</label>
              <textarea value={newMember.notes} onChange={e => setNewMember({ ...newMember, notes: e.target.value })} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', minHeight: '80px' }} />
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={addMember} style={{ flex: 1, padding: '10px', background: '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Добавить</button>
              <button onClick={() => setShowAddMember(false)} style={{ flex: 1, padding: '10px', background: '#ccc', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Отмена</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Add Event */}
      {showAddEvent && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', padding: '30px', borderRadius: '8px', width: '400px', maxHeight: '80vh', overflow: 'auto' }}>
            <h3>Добавить событие</h3>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Название:</label>
              <input type="text" value={newEvent.title} onChange={e => setNewEvent({ ...newEvent, title: e.target.value })} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }} />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Дата:</label>
              <input type="date" value={newEvent.eventDate} onChange={e => setNewEvent({ ...newEvent, eventDate: e.target.value })} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }} />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Тип:</label>
              <input type="text" placeholder="день рождения, годовщина..." value={newEvent.eventType} onChange={e => setNewEvent({ ...newEvent, eventType: e.target.value })} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }} />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Член семьи (опционально):</label>
              <select value={newEvent.familyMemberId} onChange={e => setNewEvent({ ...newEvent, familyMemberId: e.target.value })} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}>
                <option value="">Не выбрано</option>
                {familyMembers.map(member => (
                  <option key={member.id} value={member.id}>{member.name}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={addEvent} style={{ flex: 1, padding: '10px', background: '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Добавить</button>
              <button onClick={() => setShowAddEvent(false)} style={{ flex: 1, padding: '10px', background: '#ccc', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Отмена</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Edit Event */}
      {showEditEvent && editingEvent && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', padding: '30px', borderRadius: '8px', width: '400px', maxHeight: '80vh', overflow: 'auto' }}>
            <h3>Изменить событие</h3>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Название:</label>
              <input 
                type="text" 
                value={editingEvent.title} 
                onChange={e => setEditingEvent({ ...editingEvent, title: e.target.value })} 
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }} 
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Дата:</label>
              <input 
                type="date" 
                value={editingEvent.eventDate.split('T')[0]} 
                onChange={e => setEditingEvent({ ...editingEvent, eventDate: e.target.value })} 
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }} 
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Тип:</label>
              <input 
                type="text" 
                placeholder="день рождения, годовщина..." 
                value={editingEvent.eventType} 
                onChange={e => setEditingEvent({ ...editingEvent, eventType: e.target.value })} 
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }} 
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Член семьи (опционально):</label>
              <select 
                value={editingEvent.familyMember ? familyMembers.find(m => m.name === editingEvent.familyMember?.name)?.id || '' : ''} 
                onChange={e => {
                  const memberId = e.target.value;
                  const member = familyMembers.find(m => m.id === parseInt(memberId));
                  setEditingEvent({ 
                    ...editingEvent, 
                    familyMember: member ? { name: member.name } : undefined 
                  });
                }} 
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
              >
                <option value="">Не выбрано</option>
                {familyMembers.map(member => (
                  <option key={member.id} value={member.id}>{member.name}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={updateEvent} style={{ flex: 1, padding: '10px', background: '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Сохранить</button>
              <button onClick={() => { setShowEditEvent(false); setEditingEvent(null); }} style={{ flex: 1, padding: '10px', background: '#ccc', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Отмена</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Add Family Goal */}
      {showAddFamilyGoal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', padding: '30px', borderRadius: '12px', width: '450px', maxHeight: '80vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <h3 style={{ marginTop: 0, color: '#2d3748', fontSize: '24px' }}>🎯 Добавить семейную цель</h3>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#4a5568' }}>Выберите цель:</label>
              <select 
                value={newFamilyGoal.goalId} 
                onChange={e => setNewFamilyGoal({ ...newFamilyGoal, goalId: e.target.value })} 
                style={{ 
                  width: '100%', 
                  padding: '12px', 
                  borderRadius: '8px', 
                  border: '2px solid #e2e8f0',
                  fontSize: '14px',
                  background: '#f7fafc',
                  cursor: 'pointer'
                }}
              >
                <option value="">-- Выберите цель из списка --</option>
                {allGoals.map(goal => (
                  <option key={goal.id} value={goal.id}>
                    {goal.name} ({goal.status === 'completed' ? '✓ Завершена' : goal.status === 'in_progress' ? '⏳ В процессе' : '📋 Запланирована'})
                  </option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#4a5568' }}>Участники (через запятую):</label>
              <input 
                type="text" 
                placeholder="Иван, Мария, Петр" 
                value={newFamilyGoal.familyMembers} 
                onChange={e => setNewFamilyGoal({ ...newFamilyGoal, familyMembers: e.target.value })} 
                style={{ 
                  width: '100%', 
                  padding: '12px', 
                  borderRadius: '8px', 
                  border: '2px solid #e2e8f0',
                  fontSize: '14px',
                  background: '#f7fafc'
                }} 
              />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#4a5568' }}>Заметки:</label>
              <textarea 
                value={newFamilyGoal.notes} 
                onChange={e => setNewFamilyGoal({ ...newFamilyGoal, notes: e.target.value })} 
                placeholder="Дополнительная информация о семейной цели..."
                style={{ 
                  width: '100%', 
                  padding: '12px', 
                  borderRadius: '8px', 
                  border: '2px solid #e2e8f0', 
                  minHeight: '100px',
                  fontSize: '14px',
                  background: '#f7fafc',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }} 
              />
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button 
                onClick={addFamilyGoal} 
                style={{ 
                  flex: 1, 
                  padding: '12px', 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '8px', 
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '16px',
                  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
                }}
              >
                Добавить
              </button>
              <button 
                onClick={() => setShowAddFamilyGoal(false)} 
                style={{ 
                  flex: 1, 
                  padding: '12px', 
                  background: '#e2e8f0', 
                  color: '#4a5568',
                  border: 'none', 
                  borderRadius: '8px', 
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '16px'
                }}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Family;
